import { useEffect, useMemo, useState } from 'react';
import { projectGoal, type GoalMeshSource, type GoalProjection, type GoalSubject } from './goalProjection';
import type { ObjectModel } from '../types';

/**
 * The projected twin at one body fat, and whether it is on its way.
 *
 * Needs a target and a gender, not a weight: the body and its waist come from
 * the target alone. `leanHeldKg` only adds the kilograms, and a client who has
 * never been weighed still gets their goal body.
 *
 * Used twice on the dashboard: once for the target the client has, and once
 * for the value a coach is trying on the Target page before saving it.
 *
 * The model is memoised on the projection. Built fresh on every render, it
 * reached the 3D view as a new object each time, and the view reloads its mesh
 * on a new object — so every tick of the slider re-parsed and re-measured the
 * projected body in two canvases.
 */
export const useGoalProjection = (
    source: GoalMeshSource,
    targetBodyFat: number | undefined,
    leanHeldKg: number | undefined,
    subject: GoalSubject | undefined,
) => {
    const [goal, setGoal] = useState<GoalProjection | null>(null);
    const [loading, setLoading] = useState(false);
    const gender = subject?.gender;
    const heightCm = subject?.heightCm;

    useEffect(() => {
        if (!targetBodyFat || !gender) {
            setGoal(null);
            setLoading(false);
            return;
        }
        let live = true;
        setLoading(true);
        projectGoal(source, targetBodyFat, leanHeldKg, { gender, heightCm })
            .then((g) => live && setGoal(g))
            // A missing twin must not take the rest of the page down with it.
            .catch(() => live && setGoal(null))
            .finally(() => live && setLoading(false));
        return () => {
            live = false;
        };
    }, [source, targetBodyFat, leanHeldKg, gender, heightCm]);

    const model = useMemo<ObjectModel | null>(
        () =>
            goal
                ? {
                      obj: goal.obj,
                      id: `goal-${goal.targetBodyFat}`,
                      createdAt: new Date().toISOString(),
                      visibleEffects: [],
                      showMeasurement: '',
                      measurements: {} as ObjectModel['measurements'],
                  }
                : null,
        [goal],
    );

    return { goal, model, loading };
};
